import { type FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TypeAnimation } from 'react-type-animation';
import useMeasure, { type RectReadOnly } from 'react-use-measure';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { type IntroStepsConfigValue } from '@/common/config/intro-steps';
import { playSound } from '@/common/lib/soundEffect';
import { withLineBreaks } from '@/common/lib/withLinkeBreaks';
import {
  GreyRoundedButton,
  RoundedButton,
} from '@/components/ui/Button/Button';

import { IntroContext } from '../context';

import OnboardingReward from './OnboardingReward';

import css from './styles.module.scss';

interface StepProps {
  currentStep: number;
  title: string;
  text: string;
  buttonText: string;
  withNextButton?: boolean;
  withAdditionalButton?: boolean;
  toNextStep: () => void;
  config: IntroStepsConfigValue;
}

const Step: FC<StepProps> = ({
  text,
  title,
  buttonText,
  withNextButton,
  withAdditionalButton,
  toNextStep,
  currentStep,
  config,
}) => {
  const {
    highlightComponentRect,
    startOnboarding,
    isFinishOnboarding,
    acountBounds,
    tgemIconBounds,
  } = useContext(IntroContext);
  const [ref, { height }] = useMeasure();
  const { t } = useTranslation();
  const textSpeed = 80;

  const [isTyping, setIsTyping] = useState(false);

  const soundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isTyping) {
      soundIntervalRef.current = setInterval(() => {
        playSound('text/one');
      }, textSpeed);
    } else {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
    }

    return (): void => {
      if (soundIntervalRef.current) clearInterval(soundIntervalRef.current);
    };
  }, [isTyping, textSpeed]);

  const handleClick = (): (() => void) | void => {
    if (currentStep === 26) {
      return startOnboarding('finishOnboarding');
    }

    return toNextStep;
  };

  const calcPosition = ({
    margin,
    measure,
    defaultPosition,
  }: {
    margin?: number;
    measure: RectReadOnly | null;
    defaultPosition?: {
      top?: string;
      bottom?: string;
    };
  }): Record<string, string> =>
    margin && measure
      ? {
          top:
            margin < 0
              ? `${Math.max(measure.top + margin - height, 50)}px`
              : `${measure.bottom + margin}px`,
        }
      : defaultPosition
        ? defaultPosition
        : {
            top: '70%',
          };

  const firstLast: boolean = [1, 27].includes(currentStep);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      ref={ref}
      style={{
        ...calcPosition({
          margin: config.margin,
          measure: highlightComponentRect,
          defaultPosition: config.defaultPosition,
        }),
        width: 'calc(100% - 2rem)',
        ...(config.containerStyle || {}),
      }}
      className={cn(
        'absolute left-[60%] -translate-x-1/2 z-[1]',
        currentStep !== 1 && currentStep !== 27 && 'top-0',
      )}
    >
      <div className={cn(css.step, !text && 'hidden')}>
        <div className="relative flex flex-col gap-[10px]">
          {!!title && (
            <span
              className={cn(
                '!text-main-bg text-[16px] font-medium leading-1.2 invisible whitespace-pre-line bold',
                firstLast && '!text-[14px]',
              )}
            >
              {withLineBreaks(title)}
            </span>
          )}
          <span
            className={cn(
              '!text-main-bg text-[16px] font-medium leading-1.2 invisible whitespace-pre-line',
              firstLast && '!text-[14px]',
            )}
          >
            {withLineBreaks(text)}
          </span>
          {!!title && (
            <TypeAnimation
              key={`title-${currentStep}`}
              sequence={[withLineBreaks(title)]}
              wrapper="span"
              cursor={false}
              speed={textSpeed}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                display: 'block',
                fontSize: '14px',
                fontWeight: '700',
                color: 'rgb(24 26 32)',
                marginBottom: '0.5rem',
                lineHeight: '120%',
                whiteSpace: 'pre-line',
              }}
            />
          )}
          <TypeAnimation
            key={`text-${currentStep}`}
            sequence={[
              (): void => setIsTyping(true),
              withLineBreaks(text),
              (): void => setIsTyping(false),
            ]}
            wrapper="span"
            cursor={false}
            speed={textSpeed}
            style={{
              position: 'absolute',
              top: title ? '25px' : 0,
              left: 0,
              display: 'block',
              fontSize: firstLast ? '14px' : '16px',
              fontWeight: '500',
              color: 'rgb(24 26 32)',
              lineHeight: '120%',
              whiteSpace: 'pre-line',
            }}
          />
          {firstLast && (
            <OnboardingReward
              mainText={currentStep === 1 ? 'Rewards' : 'Claim your rewards'}
              isStart={currentStep === 1 ? true : false}
              isFinishOnboarding={isFinishOnboarding}
              tgemIconBounds={tgemIconBounds}
              acountBounds={acountBounds}
            />
          )}
        </div>
        {withNextButton && (
          <RoundedButton
            id="intro-next-button"
            onClick={handleClick}
            className="absolute right-[1rem] bottom-0 translate-y-1/2 pointer-events-auto"
            classNamesText={cn(firstLast && '!text-[14px]')}
            text={t(buttonText)}
          ></RoundedButton>
        )}
        {withAdditionalButton && (
          <GreyRoundedButton
            id="intro-additional-button"
            className="absolute left-[1rem] bottom-0 translate-y-1/2 pointer-events-auto flex justify-center"
            classNamesText={cn(firstLast && '!text-[14px]')}
            text={t('Skip reward')}
          ></GreyRoundedButton>
        )}
      </div>
    </motion.div>
  );
};

export { Step };
